import { useEffect } from 'react';

import { AppIcon, type ContentNugget, NUGGET_TYPES, NUGGET_VARIANTS } from '@backrs_ui/common-ui';

import calendarOfMilestonesImage from '@backrs_ui/grow-ui/src/assets/images/success-system/calendar-of-milestones.png';
import successSystemBanner from '@backrs_ui/grow-ui/src/assets/images/success-system/success-system-banner.svg';
import {
  GUIDE_ASIDE_WIDTH,
  GUIDE_TEXT_MAX_WIDTH,
  GuideNuggetRow,
  GuidePageShell,
  SectionHeader,
} from '@backrs_ui/grow-ui/src/components/GuidePage';
import { LubavButton } from '@backrs_ui/grow-ui/src/components/Lubav/LubavButton';
import {
  SUCCESS_SYSTEM_SECTION_IDS,
  successSystemContent,
  type SuccessSystemGroup,
  type SuccessSystemLubav,
} from '@backrs_ui/grow-ui/src/pages/SuccessSystem/successSystem.content';
import { useAppDispatch } from '@backrs_ui/grow-ui/src/redux/hooks';
import { closeCustomTab, openCustomTab } from '@backrs_ui/grow-ui/src/redux/tabs/tabs.slice';

import { Box, SxProps, Theme, Typography } from '@mui/material';
import type { SystemStyleObject } from '@mui/system';

import { FourUnlocksGrid } from './components/FourUnlocksGrid';
import { SuccessSystemDiagram } from './components/SuccessSystemDiagram';
import { SuccessSystemTopic } from './components/SuccessSystemTopic';
import { useAgentContext } from './SuccessSystemPageContainer.agentContext';

type StyleFn = (theme: Theme) => SystemStyleObject<Theme>;

const { bannerAlt, overview, lifeAspirations, fundamentals, bigDecisions, trackProgression } =
  successSystemContent;

const CALENDAR_TAB_ID = 'calendarOfMilestones';

const CALENDAR_NUGGET: ContentNugget = {
  id: 0,
  title: trackProgression.calendarOfMilestones.title,
  description: trackProgression.calendarOfMilestones.body,
  slug: 'calendar-of-milestones',
  star_rating: 0,
  value_rating: 0,
  variant: NUGGET_VARIANTS.TOOL,
  is_browsable: false,
  type: NUGGET_TYPES.TOOL,
  preview_media: { id: 0, media_url: calendarOfMilestonesImage },
};

const SuccessSystemLubavButton: React.FC<Readonly<{ id: string; lubav: SuccessSystemLubav }>> = ({
  id,
  lubav,
}) => (
  <LubavButton
    id={id}
    label={lubav.label}
    prompt={`${lubav.label} Answer conversationally, in a few short paragraphs with no headings, drawing on these points about the Backrs Success System:\n\n${lubav.talkingPoints}`}
    allowTools={false}
  />
);

const InfoHint: React.FC<Readonly<{ id: string; label: string }>> = ({ id, label }) => (
  <Box id={id} sx={infoHintSx}>
    <AppIcon iconName="InfoCircle" diameter="20px" color="currentColor" />
    <Typography variant="text-sm-semibold">{label}</Typography>
  </Box>
);

const GroupHeader: React.FC<Readonly<{ group: SuccessSystemGroup }>> = ({ group }) => {
  let rightSlot: React.ReactNode = null;

  if (group.lubav) {
    rightSlot = <SuccessSystemLubavButton id={`${group.id}-lubav-btn`} lubav={group.lubav} />;
  } else if (group.infoLabel) {
    rightSlot = <InfoHint id={`${group.id}-info-hint`} label={group.infoLabel} />;
  }

  return (
    <Box id={group.id} sx={anchorTargetSx}>
      <SectionHeader title={group.title} subtitle={group.subtitle} rightSlot={rightSlot} />
    </Box>
  );
};

export const SuccessSystemPageContainer = () => {
  useAgentContext();
  const dispatch = useAppDispatch();

  useEffect(
    () => () => {
      dispatch(closeCustomTab(CALENDAR_TAB_ID));
    },
    [dispatch],
  );

  return (
    <GuidePageShell
      toolId="success_system"
      banner={{
        src: successSystemBanner,
        alt: bannerAlt,
        hideOverlays: true,
        backgroundColor: 'Brand/900',
      }}
    >
      <Box sx={bodySx}>
        <Box id={SUCCESS_SYSTEM_SECTION_IDS.overview}>
          <SectionHeader
            title={overview.title}
            hideDivider
            rightSlot={
              <SuccessSystemLubavButton
                id="success-system-overview-lubav-btn"
                lubav={overview.lubav}
              />
            }
          />
          <Box sx={introSx}>
            <Box sx={introTextSx}>
              {overview.description.map((paragraph) => (
                <Typography key={paragraph} variant="text-md-regular" sx={textTertiarySx}>
                  {paragraph}
                </Typography>
              ))}
            </Box>
            <Box sx={introDiagramSx}>
              <SuccessSystemDiagram />
            </Box>
          </Box>
        </Box>

        <Box sx={blockSx}>
          <GroupHeader group={lifeAspirations.group} />
          <SuccessSystemTopic topic={lifeAspirations.lifeBlueprint} />
        </Box>

        <Box sx={blockSx}>
          <GroupHeader group={fundamentals.group} />
          <Box id={fundamentals.fourUnlocks.id} sx={[topicStackSx, anchorTargetSx]}>
            <Box sx={splitSx}>
              <Box sx={splitTextSx}>
                <Typography variant="text-md-semibold" sx={topicTitleSx}>
                  {fundamentals.fourUnlocks.title}
                </Typography>
                <Typography variant="text-md-regular" sx={textTertiarySx}>
                  {fundamentals.fourUnlocks.body}
                </Typography>
              </Box>
              <Box sx={asideQuoteSx}>
                <Typography variant="text-md-regular">{fundamentals.fourUnlocks.aside}</Typography>
              </Box>
            </Box>
            <FourUnlocksGrid unlocks={fundamentals.fourUnlocks.unlocks} />
          </Box>
          <SuccessSystemTopic topic={fundamentals.knowYourStrengths} />
        </Box>

        <Box sx={blockSx}>
          <GroupHeader group={bigDecisions.group} />
          {bigDecisions.topics.map((topic) => (
            <SuccessSystemTopic key={topic.id} topic={topic} />
          ))}
        </Box>

        <Box sx={blockSx}>
          <GroupHeader group={trackProgression.group} />
          <SuccessSystemTopic topic={trackProgression.investWhereItCounts} />
          <SuccessSystemTopic
            topic={trackProgression.calendarOfMilestones}
            aside={
              <GuideNuggetRow
                nuggets={[CALENDAR_NUGGET]}
                onNuggetClick={() => dispatch(openCustomTab(CALENDAR_TAB_ID))}
                direction="column"
              />
            }
          />
        </Box>
      </Box>
    </GuidePageShell>
  );
};

const bodySx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-4xl'],
  pt: backrs.spacing['spacing-3xl'],
});

const infoHintSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-xs'],
  flexShrink: 0,
  px: backrs.spacing['spacing-lg'],
  py: backrs.spacing['spacing-md'],
  borderRadius: backrs.radius['radius-md'],
  border: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  color: backrs.colors['Colors/Text/text-secondary'],
});

const blockSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-3xl'],
});

const anchorTargetSx: StyleFn = ({ backrs }) => ({
  scrollMarginTop: backrs.spacing['spacing-3xl'],
});

const introSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'flex-start',
  gap: backrs.spacing['spacing-xl'],
  pt: backrs.spacing['spacing-xl'],
});

const introTextSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xl'],
  flex: '1 1 320px',
  maxWidth: GUIDE_TEXT_MAX_WIDTH,
});

const introDiagramSx: SxProps<Theme> = {
  flex: '1 1 320px',
  minWidth: 0,
};

const topicStackSx: StyleFn = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-5xl'],
});

const splitSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: backrs.spacing['spacing-3xl'],
});

const splitTextSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xl'],
  flex: '1 1 320px',
  maxWidth: GUIDE_TEXT_MAX_WIDTH,
});

const asideQuoteSx: SxProps<Theme> = ({ backrs }) => ({
  flex: `0 1 ${GUIDE_ASIDE_WIDTH}`,
  p: backrs.spacing['spacing-xl'],
  borderRadius: backrs.radius['radius-xl'],
  backgroundColor: backrs.colors['Colors/Background/bg-secondary'],
  color: backrs.colors['Colors/Text/text-tertiary'],
  fontStyle: 'italic',
});

const topicTitleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-brand-secondary'],
});

const textTertiarySx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});
